<template>
  <p
    class="text-static-brand-mid font-weight-body-2-bold flex text-[42px] leading-[42px] ease-out motion-safe:transition-transform motion-safe:duration-300"
    :class="popCurrentPrice ? 'scale-110' : 'scale-100'"
    data-test="intro-atc-enhanced-price-animation"
  >
    <span>
      {{
        i18n.price(currentPrice, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
      }}
    </span>
    <span v-if="displayAsterix" class="heading-2">*</span>
  </p>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, toRaw } from 'vue'

import type { Price } from '@backmarket/http-api/src/api-models/Price'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { cloneDeep } from '@backmarket/utils/object/cloneDeep'

type AnimatedPriceProps = {
  endPrice: Price
  startPrice: Price
  startAnimation?: boolean
  displayAsterix?: boolean
  animationDelay?: number
}

const ANIMATION_START_DELAY = 1000
const ANIMATION_DURATION = 1200
const POP_ANIMATION_DELAY = 600

const {
  endPrice,
  startPrice,
  startAnimation = true,
  displayAsterix = true,
  animationDelay = ANIMATION_START_DELAY,
} = defineProps<AnimatedPriceProps>()

const i18n = useI18n()

const currentPrice = ref<Price>(cloneDeep(toRaw(startPrice)))
const popCurrentPrice = ref(false)

let rolldownTimeout: ReturnType<typeof setTimeout> | null = null
let popCurrentPriceTimeout: ReturnType<typeof setTimeout> | null = null

onMounted(() => {
  if (!startAnimation) return

  function startRolldownAnimation() {
    if (!startPrice?.amount) return
    if (!endPrice?.amount) return

    const startTime = Date.now()

    const reducedPrice = parseFloat(endPrice.amount)
    const fullPrice = parseFloat(startPrice.amount)
    const priceDelta = fullPrice - reducedPrice

    function animate() {
      if (!currentPrice.value) return

      const elapsed = Date.now() - startTime
      let progress = Math.min(elapsed / ANIMATION_DURATION, 1)

      const easeOut = 1 - (1 - progress) ** 3
      const newPrice = Math.round(fullPrice - priceDelta * easeOut)

      // should never happen, but as a failsafe...
      if (newPrice < 0) {
        progress = 1
        currentPrice.value.amount = '0'
      } else {
        currentPrice.value.amount = newPrice.toString()
      }

      if (progress < 1) requestAnimationFrame(animate)
      else {
        popCurrentPrice.value = true

        popCurrentPriceTimeout = setTimeout(() => {
          popCurrentPrice.value = false
        }, POP_ANIMATION_DELAY)
      }
    }

    animate()
  }

  rolldownTimeout = setTimeout(startRolldownAnimation, animationDelay)
})

onUnmounted(() => {
  if (rolldownTimeout) clearTimeout(rolldownTimeout)
  if (popCurrentPriceTimeout) clearTimeout(popCurrentPriceTimeout)
})
</script>
